export const MAX_HTML_FILE_MB = 5
export const MAX_HTML_FILE_BYTES = MAX_HTML_FILE_MB * 1024 * 1024
export const MAX_HTML_CHARS = 250_000

export type HtmlToPdfErrorCode =
  | 'empty_html'
  | 'too_large'
  | 'unsupported_file'
  | 'unsupported_browser'
  | 'conversion_error'

export class HtmlToPdfError extends Error {
  constructor(public readonly code: HtmlToPdfErrorCode) {
    super(code)
    this.name = 'HtmlToPdfError'
  }
}

export function isSupportedHtmlFile(file: File): boolean {
  const lowerName = file.name.toLowerCase()
  return (
    file.type === 'text/html' ||
    file.type === 'application/xhtml+xml' ||
    lowerName.endsWith('.html') ||
    lowerName.endsWith('.htm')
  )
}

export function validateHtmlInput(html: string): void {
  if (html.trim().length === 0) {
    throw new HtmlToPdfError('empty_html')
  }
  if (html.length > MAX_HTML_CHARS) {
    throw new HtmlToPdfError('too_large')
  }
}

export async function readHtmlFile(file: File): Promise<string> {
  if (!isSupportedHtmlFile(file)) {
    throw new HtmlToPdfError('unsupported_file')
  }
  if (file.size > MAX_HTML_FILE_BYTES) {
    throw new HtmlToPdfError('too_large')
  }
  return file.text()
}

export function htmlPdfFilename(sourceName: string | null): string {
  if (!sourceName) return 'html-to-pdf.pdf'
  const baseName = sourceName.replace(/\.[^.]+$/, '').trim()
  return `${baseName || 'html-to-pdf'}.pdf`
}

function assertBrowserSupport() {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof Blob === 'undefined' ||
    typeof URL === 'undefined'
  ) {
    throw new HtmlToPdfError('unsupported_browser')
  }
}

function createRenderHost(html: string): HTMLElement {
  const host = document.createElement('article')
  host.className = 'html-v-pdf-render-host'
  host.innerHTML = html
  Object.assign(host.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: '794px',
    maxWidth: '794px',
    minHeight: '1123px',
    padding: '48px',
    background: '#ffffff',
    color: '#111827',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    lineHeight: '1.5',
    boxSizing: 'border-box',
  })
  document.body.appendChild(host)
  return host
}

export async function htmlToPdf(html: string): Promise<Blob> {
  assertBrowserSupport()
  validateHtmlInput(html)

  const host = createRenderHost(html)

  try {
    const module = await import('html2pdf.js')
    const html2pdf = module.default ?? module
    const worker = html2pdf()
      .set({
        margin: 10,
        filename: 'html-to-pdf.pdf',
        image: { type: 'jpeg', quality: 0.96 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 794,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
      })
      .from(host)
      .toPdf()
      .outputPdf('blob')

    const blob = await worker
    if (!(blob instanceof Blob)) {
      throw new HtmlToPdfError('conversion_error')
    }
    return blob
  } catch (error) {
    if (error instanceof HtmlToPdfError) {
      throw error
    }
    throw new HtmlToPdfError('conversion_error')
  } finally {
    host.remove()
  }
}
